import { useCallback, useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function usePwaInstall() {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("pwa-install-dismissed") === "1";
    } catch {
      return false;
    }
  });
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = useCallback(async () => {
    if (!prompt) return;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setPrompt(null);
  }, [prompt]);

  const dismiss = useCallback(() => {
    setPrompt(null);
    setDismissed(true);
    try {
      localStorage.setItem("pwa-install-dismissed", "1");
    } catch {}
  }, []);

  return { canInstall: !!prompt && !dismissed, install, dismiss };
}
